"use client";

import { useLocale } from "@i18n/react";
import { useSearch } from "@packages/framework";
import { useQueryClient } from "@tanstack/react-query";

import type { UseRequestMutationProps } from "../../../../../request/hooks/useRequestMutation";
import { useRequestMutation } from "../../../../../request/hooks/useRequestMutation";
import { keyMe } from "../../../me/keyMe";
import { keyAdminDashUsers } from "../../members/keyAdminDashUsers";
import { keyCommunityMember } from "../../members/keyCommunityMember";
import { keyCommunitiesAdminCommunities } from "../keyCommunitiesAdminCommunities";
import { keyCommunitiesAdminCommunity } from "../keyCommunitiesAdminCommunity";

export type UseRequestCommunitiesAdminMemberProps = {
  communityId: number;
  userId: number;
  mutationProps?: UseRequestMutationProps;
};

export const useRequestCommunitiesAdminMember = ({
  communityId,
  userId,
  mutationProps = {},
}: UseRequestCommunitiesAdminMemberProps) => {
  const locale = useLocale();
  const queryClient = useQueryClient();
  const search = useSearch();

  return {
    mutationDelete: useRequestMutation({
      ...mutationProps,
      mutationFnRequestProps: () => ({
        method: "DELETE",
        urlBackendPath: `/communities/${communityId}/members/${userId}`,
      }),
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({
          queryKey: [keyCommunitiesAdminCommunity({ id: communityId }), locale],
        });
        queryClient.invalidateQueries({
          queryKey: [keyCommunitiesAdminCommunities(), locale],
        });
        queryClient.invalidateQueries({
          queryKey: [
            keyAdminDashUsers({
              id: communityId,
              search: search.get("search"),
              filter_by_is_admin:
                search.get("filter") === "members" ? "member" : "admin",
            }),
            locale,
          ],
        });
        queryClient.invalidateQueries({ queryKey: [keyMe(), locale] });
        mutationProps?.onSuccess(data, variables, context);
      },
    }),
    mutationPostAdminGrant: useRequestMutation({
      ...mutationProps,
      mutationFnRequestProps: () => ({
        urlBackendPath: `/me/communities/${communityId}/admin/${userId}/grant`,
      }),
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({
          queryKey: [
            keyAdminDashUsers({
              id: communityId,
              search: search.get("search"),
              filter_by_is_admin: "member",
            }),
            locale,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            keyAdminDashUsers({
              id: communityId,
              search: search.get("search"),
              filter_by_is_admin: "admin",
            }),
            locale,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [keyCommunityMember({ communityId, userId }), locale],
        });
        mutationProps?.onSuccess(data, variables, context);
      },
    }),
    mutationPostAdminRevoke: useRequestMutation({
      ...mutationProps,
      mutationFnRequestProps: () => ({
        urlBackendPath: `/me/communities/${communityId}/admin/${userId}/revoke`,
      }),
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({
          queryKey: [
            keyAdminDashUsers({
              id: communityId,
              search: search.get("search"),
              filter_by_is_admin: "member",
            }),
            locale,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            keyAdminDashUsers({
              id: communityId,
              search: search.get("search"),
              filter_by_is_admin: "admin",
            }),
            locale,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [keyCommunityMember({ communityId, userId }), locale],
        });
        queryClient.invalidateQueries({ queryKey: [keyMe(), locale] });
        mutationProps?.onSuccess(data, variables, context);
      },
    }),
  };
};
